import { type ReactNode, useState } from 'react'
import { Head, Link, router, useForm } from '@inertiajs/react'
import {
  ArrowLeft,
  Calendar,
  Download,
  Gift,
  HandHeart,
  Mail,
  Pencil,
  Trash2,
  UserRound,
  X,
} from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TagCombobox } from '@/components/TagCombobox'
import NotesAutosave from '@/components/crm/NotesAutosave'
import type { GuestDetail, GuestPurchaseRow } from '@/types'

type AvailableEvent = {
  id: string
  title: string
  date: string | null
  archived: boolean
}

type Props = {
  guest: GuestDetail
  purchases: GuestPurchaseRow[]
  available_tags: string[]
  available_events: AvailableEvent[]
}

function dollars(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function formatMonthYear(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-US', { year: 'numeric', month: 'short' })
}

const SOURCE_LABEL: Record<GuestDetail['source'], string> = {
  purchase: 'Purchaser',
  manual: 'Manual',
  import: 'Imported',
}

const KIND_LABEL: Record<GuestPurchaseRow['kind'], string> = {
  ticket: 'Ticket',
  sponsorship: 'Sponsorship',
  ad: 'Ad',
  donation: 'Donation',
}

const STATUS_VARIANT: Record<GuestPurchaseRow['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
  paid: 'default',
  refunded: 'destructive',
  partially_refunded: 'secondary',
  failed: 'destructive',
  pending: 'outline',
  expired: 'outline',
}

function initials(guest: GuestDetail) {
  const first = guest.first_name?.[0] ?? ''
  const last = guest.last_name?.[0] ?? ''
  const combined = `${first}${last}`.trim()
  return (combined || guest.email[0] || '?').toUpperCase()
}

export default function DashboardGuestsShow({ guest, purchases, available_tags, available_events }: Props) {
  const [editing, setEditing] = useState(false)
  const { data, setData, patch, processing, errors, reset } = useForm({
    guest: {
      first_name: guest.first_name ?? '',
      last_name: guest.last_name ?? '',
      email: guest.email,
      phone: guest.phone ?? '',
      address_line1: guest.address_line1 ?? '',
      address_line2: guest.address_line2 ?? '',
      city: guest.city ?? '',
      region: guest.region ?? '',
      postal_code: guest.postal_code ?? '',
      country: guest.country ?? '',
      tags: guest.tags,
    },
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    patch(`/dashboard/guests/${guest.id}`, {
      preserveScroll: true,
      onSuccess: () => setEditing(false),
    })
  }

  function cancelEditing() {
    reset()
    setEditing(false)
  }

  const activityTotalCents = purchases.reduce((sum, p) => sum + p.amount_cents, 0)

  return (
    <>
      <Head title={guest.display_name} />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          href="/dashboard/guests"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to transactions
        </Link>

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar size="lg" className="size-12 bg-primary/10">
              <AvatarFallback className="bg-primary/10 font-medium text-primary">
                {initials(guest)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-tight">{guest.display_name}</h1>
                <Badge variant="secondary">{SOURCE_LABEL[guest.source]}</Badge>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{guest.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={`mailto:${guest.email}`}>
                <Mail className="size-4" />
                Email
              </a>
            </Button>
            {editing ? (
              <Button variant="outline" size="sm" onClick={cancelEditing}>
                <X className="size-4" />
                Cancel
              </Button>
            ) : (
              <Button size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-4" />
                Edit
              </Button>
            )}
            <DeleteGuestButton guestId={guest.id} hasPurchases={purchases.length > 0} />
          </div>
        </div>

        {/* Stat tiles */}
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatTile label="Total given" value={dollars(guest.total_purchase_amount_cents)} icon={<HandHeart className="size-4" />} />
          <StatTile label="Donations" value={guest.purchase_count} icon={<Gift className="size-4" />} />
          <StatTile label="Last donation" value={formatDate(guest.last_purchase_at)} icon={<Calendar className="size-4" />} />
          <StatTile label="Supporter since" value={formatMonthYear(guest.created_at)} icon={<UserRound className="size-4" />} />
        </div>

        {/* Contact + Notes */}
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Contact</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="First name" id="first_name" value={data.guest.first_name}
                           onChange={v => setData('guest', { ...data.guest, first_name: v })}
                           error={errors['guest.first_name']} />
                    <Field label="Last name" id="last_name" value={data.guest.last_name}
                           onChange={v => setData('guest', { ...data.guest, last_name: v })}
                           error={errors['guest.last_name']} />
                  </div>
                  <Field label="Email" id="email" required value={data.guest.email}
                         onChange={v => setData('guest', { ...data.guest, email: v })}
                         error={errors['guest.email']} />
                  <Field label="Phone" id="phone" value={data.guest.phone}
                         onChange={v => setData('guest', { ...data.guest, phone: v })}
                         error={errors['guest.phone']} />
                  <Field label="Address line 1" id="address_line1" value={data.guest.address_line1}
                         onChange={v => setData('guest', { ...data.guest, address_line1: v })}
                         error={errors['guest.address_line1']} />
                  <Field label="Address line 2" id="address_line2" value={data.guest.address_line2}
                         onChange={v => setData('guest', { ...data.guest, address_line2: v })}
                         error={errors['guest.address_line2']} />
                  <div className="grid grid-cols-3 gap-4">
                    <Field label="City" id="city" value={data.guest.city}
                           onChange={v => setData('guest', { ...data.guest, city: v })}
                           error={errors['guest.city']} />
                    <Field label="State" id="region" value={data.guest.region}
                           onChange={v => setData('guest', { ...data.guest, region: v })}
                           error={errors['guest.region']} />
                    <Field label="Postal code" id="postal_code" value={data.guest.postal_code}
                           onChange={v => setData('guest', { ...data.guest, postal_code: v })}
                           error={errors['guest.postal_code']} />
                  </div>
                  <Field label="Country" id="country" value={data.guest.country}
                         onChange={v => setData('guest', { ...data.guest, country: v })}
                         error={errors['guest.country']} />

                  <TagCombobox
                    label="Tags"
                    placeholder="Search or add a tag…"
                    suggestions={available_tags}
                    selected={data.guest.tags}
                    onChange={(tags) => setData('guest', { ...data.guest, tags })}
                    allowCustom
                  />

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={cancelEditing} disabled={processing}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing}>
                      {processing ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                    <ReadField label="First name" value={guest.first_name} />
                    <ReadField label="Last name" value={guest.last_name} />
                    <ReadField label="Email" value={guest.email} />
                    <ReadField label="Phone" value={guest.phone} />
                    <ReadField label="Address line 1" value={guest.address_line1} />
                    <ReadField label="Address line 2" value={guest.address_line2} />
                    <ReadField label="City" value={guest.city} />
                    <ReadField label="State" value={guest.region} />
                    <ReadField label="Postal code" value={guest.postal_code} />
                    <ReadField label="Country" value={guest.country} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tags</p>
                    {guest.tags.length === 0 ? (
                      <p className="mt-1 text-sm text-foreground">—</p>
                    ) : (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {guest.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="capitalize">{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <NotesAutosave guestId={guest.id} initialValue={guest.notes ?? ''} />
            </CardContent>
          </Card>
        </div>

        {/* Donor activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Donor Activity</CardTitle>
            {purchases.length > 0 && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/dashboard/guests/${guest.id}/export_activity`}>
                  <Download className="size-4" />
                  Export
                </a>
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {purchases.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(p.paid_at ?? p.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {editing && p.kind === 'donation' ? (
                          <Select
                            value={p.event?.id ?? ''}
                            onValueChange={(eventId) =>
                              router.patch(`/dashboard/purchases/${p.id}`, { purchase: { event_id: eventId } }, { preserveScroll: true })
                            }
                          >
                            <SelectTrigger className="h-8 w-56">
                              <SelectValue placeholder="Select event" />
                            </SelectTrigger>
                            <SelectContent position="popper">
                              <SelectGroup>
                                {available_events.map(ev => (
                                  <SelectItem key={ev.id} value={ev.id} disabled={ev.archived}>
                                    {ev.title}{ev.archived ? ' (archived)' : ''}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        ) : (
                          p.event ? p.event.title : <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[p.status]} className="capitalize">
                          {KIND_LABEL[p.kind]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <div>{dollars(p.amount_cents)}</div>
                        {p.refunded_amount_cents > 0 && (
                          <div className="text-xs text-muted-foreground">Refunded {dollars(p.refunded_amount_cents)}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 hover:bg-transparent">
                    <TableCell colSpan={3} className="font-semibold">Total</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-primary">
                      {dollars(activityTotalCents)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function StatTile({ label, value, icon }: { label: string; value: string | number; icon: ReactNode }) {
  return (
    <div className="rounded-xl border border-black/10 bg-card p-4 shadow-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function ReadField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm text-foreground">{value?.trim() ? value : '—'}</p>
    </div>
  )
}

function Field({ label, id, value, onChange, error, required }: { label: string; id: string; value: string; onChange: (v: string) => void; error?: string | string[]; required?: boolean }) {
  const message = Array.isArray(error) ? error[0] : error
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}{required && <span className="text-destructive"> *</span>}</Label>
      <Input id={id} value={value} onChange={e => onChange(e.target.value)} required={required} />
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  )
}

function DeleteGuestButton({ guestId, hasPurchases }: { guestId: string; hasPurchases: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Trash2 className="size-4" />
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this guest?</DialogTitle>
          <DialogDescription>
            {hasPurchases
              ? "Guests with attached purchases can't be deleted — purchases would lose their owner."
              : 'This removes the guest from your CRM. This action cannot be undone.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={hasPurchases}
            onClick={() => router.delete(`/dashboard/guests/${guestId}`)}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

DashboardGuestsShow.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
